/**
 * SF-4825: the companies a platform super admin can see in the web app, on their admin app user
 * page. Staff who may edit users can change the list within the limits sf-api sends
 * (`edit.mode`); the server checks every save again.
 *
 * State is keyed by the person's email, and a save that finishes after the page moved to another
 * person updates that first person's cached list but shows nothing on the new page.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { InfiniteCompanySelect } from './InfiniteCompanySelect';
import {
  companyListQueryKey,
  errorMessage,
  errorStatus,
  normaliseEmail,
  COMPANY_OPTIONS_QUERY_KEY,
  useCompanyList,
  useFetchCompanyOptionsPage,
  useSaveCompanies,
  type CompanyListAnswer,
  type CompanyListView,
  type ListedCompany,
  type SaveCompaniesBody,
} from '../api/superAdminCompaniesClient';

const companyLabel = (company: ListedCompany) => company.name ?? `Unknown company (${company.id})`;

interface Choice {
  value: string;
  label: React.ReactNode;
}

interface EditCompaniesModalProps {
  email: string;
  view: CompanyListView;
  saving: boolean;
  onCancel: () => void;
  onSave: (body: SaveCompaniesBody) => void;
}

const EditCompaniesModal: React.FC<EditCompaniesModalProps> = ({
  email,
  view: viewWhenOpened,
  saving,
  onCancel,
  onSave,
}) => {
  // Edit the view this window was opened from, even if the card reads a newer one meanwhile:
  // its revision goes back with the save, so sf-api refuses it if the list changed (409).
  const [view] = useState(viewWhenOpened);
  const { mode, canSetAll } = view.edit;
  const current = view.companies.map((c) => ({ value: c.id, label: companyLabel(c) }));
  const currentIds = new Set(current.map((c) => c.value));
  const [all, setAll] = useState(mode === 'any' && canSetAll && view.allCompanies);
  // Mode "add" starts with nothing new; "any" and "remove" start from the current list.
  const [selected, setSelected] = useState<Choice[]>(mode === 'add' ? [] : current);
  const fetchOptionsPage = useFetchCompanyOptionsPage();

  const remoteSearch = mode === 'any' || mode === 'add';

  const selectedIds = selected.map((s) => s.value);
  let companyIds = selectedIds;
  if (mode === 'add')
    companyIds = [...currentIds, ...selectedIds.filter((id) => !currentIds.has(id))];

  let canSave = false;
  if (mode === 'any') canSave = all || selectedIds.length > 0;
  if (mode === 'add') canSave = selectedIds.length > 0;
  if (mode === 'remove') canSave = selectedIds.length > 0 && selectedIds.length < currentIds.size;

  const handleOk = () =>
    onSave({
      email,
      allCompanies: mode === 'any' && all,
      companyIds: mode === 'any' && all ? [] : companyIds,
      revision: view.revision,
    });

  return (
    <Modal
      open
      title="Edit companies"
      okText="Save"
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={saving}
      okButtonProps={{ disabled: !canSave, 'aria-busy': saving }}
      maskClosable={false}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {mode === 'any' && canSetAll && (
          <Space>
            <Switch
              aria-label="All companies"
              checked={all}
              onChange={(checked) => {
                setAll(checked);
                if (checked) setSelected([]);
              }}
            />
            <span>All companies</span>
          </Space>
        )}
        {mode === 'add' && (
          <>
            <Typography.Text type="secondary">
              You can add companies from your own list. You can&apos;t remove this person&apos;s
              existing companies.
            </Typography.Text>
            <div>
              {current.map((c) => (
                <Tag key={c.value}>{c.label}</Tag>
              ))}
            </div>
          </>
        )}
        {mode === 'remove' && (
          <Typography.Text type="secondary">
            You can remove companies from your own list. At least one must stay.
          </Typography.Text>
        )}
        {remoteSearch ? (
          <InfiniteCompanySelect
            aria-label="Companies"
            mode="multiple"
            labelInValue
            style={{ width: '100%' }}
            placeholder={mode === 'add' ? 'Add companies' : 'Choose companies'}
            disabled={all || saving}
            value={selected}
            onChange={(values: Choice[]) => setSelected(values)}
            queryKey={COMPANY_OPTIONS_QUERY_KEY}
            fetchPage={fetchOptionsPage}
            enabled={!all}
            // Mode add offers only companies the person does not have yet.
            hiddenIds={mode === 'add' ? currentIds : undefined}
          />
        ) : (
          <Select<Choice[]>
            aria-label="Companies"
            mode="multiple"
            labelInValue
            showSearch
            style={{ width: '100%' }}
            placeholder="Choose companies"
            disabled={saving}
            value={selected}
            onChange={(values) => setSelected(values)}
            options={current}
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.trim().toLowerCase())
            }
            notFoundContent="No matching companies"
          />
        )}
      </Space>
    </Modal>
  );
};

interface SuperAdminCompaniesCardProps {
  email: string;
  canManageUsers: boolean;
}

export const SuperAdminCompaniesCard: React.FC<SuperAdminCompaniesCardProps> = ({
  email,
  canManageUsers,
}) => {
  const normalised = normaliseEmail(email);
  const query = useCompanyList(normalised);
  const save = useSaveCompanies();
  const queryClient = useQueryClient();
  // Which person the edit window is open for, and which one a save is running for.
  const [editFor, setEditFor] = useState<string | null>(null);
  const [savingFor, setSavingFor] = useState<string | null>(null);
  const shownEmailRef = useRef<string | null>(normalised);

  useEffect(() => {
    shownEmailRef.current = normalised;
    return () => {
      shownEmailRef.current = null;
    };
  }, [normalised]);

  const handleSave = async (body: SaveCompaniesBody) => {
    const origin = body.email;
    const key = companyListQueryKey(origin);
    const stillShown = () => shownEmailRef.current === origin;
    const closeFor = (prev: string | null) => (prev === origin ? null : prev);
    setSavingFor(origin);
    try {
      const view = await save(body);
      // A read begun before the save must not put the old list back over the saved one.
      await queryClient.cancelQueries({ queryKey: key });
      if (view) queryClient.setQueryData<CompanyListAnswer>(key, { kind: 'view', view });
      void queryClient.invalidateQueries({ queryKey: key });
      setEditFor(closeFor);
      if (stillShown()) message.success('Companies updated');
    } catch (error) {
      const changedMeanwhile = errorStatus(error) === 409;
      if (changedMeanwhile) {
        void queryClient.invalidateQueries({ queryKey: key });
        // The window was filled from the old list; reopen it on the fresh one.
        setEditFor(closeFor);
      }
      if (stillShown())
        message.error(errorMessage(error, 'Could not save companies. Please try again.'));
    } finally {
      setSavingFor(closeFor);
    }
  };

  const answer = query.data;
  if (answer?.kind === 'notSuperAdminCaller') return null;

  const view = answer?.kind === 'view' ? answer.view : null;
  const canEdit = canManageUsers && !!view?.edit.allowed && !query.isError;

  let body: React.ReactNode;
  if (query.isLoading) {
    body = <Spin />;
  } else if (!view) {
    body = (
      <Space>
        <Typography.Text type="danger">Could not load companies</Typography.Text>
        <Button size="small" onClick={() => query.refetch()}>
          Retry
        </Button>
      </Space>
    );
  } else if (!view.isSuperAdmin) {
    body = (
      <Typography.Text type="secondary">
        Not a super admin in the web app, so no company list applies.
      </Typography.Text>
    );
  } else if (view.allCompanies) {
    body = <Tag color="blue">All companies</Tag>;
  } else {
    body = (
      <div>
        {view.companies.map((c) => (
          <Tag key={c.id}>{companyLabel(c)}</Tag>
        ))}
      </div>
    );
  }

  return (
    <Card
      title="Companies"
      extra={
        canEdit ? (
          <Button icon={<EditOutlined />} onClick={() => setEditFor(normalised)}>
            Edit companies
          </Button>
        ) : null
      }
      style={{ marginTop: 16 }}
    >
      {view && query.isError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not refresh. Showing what was last loaded."
          action={
            <Button size="small" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        />
      )}
      {body}
      {view && canEdit && editFor === normalised && (
        <EditCompaniesModal
          email={normalised}
          view={view}
          saving={savingFor === normalised}
          onCancel={() => setEditFor(null)}
          onSave={handleSave}
        />
      )}
    </Card>
  );
};
